/** @jsxImportSource @emotion/react */
import Root from "pages/root";
import {
  BottonContainer,
  Container,
  DeleteBtn,
  EditContainer,
  IconContainer,
  ProfileContainer,
  ProfileImg,
  Rectangle,
  TitleContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadImageToS3 } from "apis/uploadImage";
import { useToast } from "hook/useToast";
import WorkspaceExitModal from "./workspace-exit-modal";
import { updateMyMemberInfo } from "apis/members/my";
import { exitWorkspace } from "apis/auth/member";
import { useRecoilState, useRecoilValue } from "recoil";
import { workspace } from "recoil/recoilState";

const MyInfoEdit = () => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const workspaceInfo = useRecoilValue(workspace);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [memberName, setMemberName] = useState<string>("");
  const [memberImage, setMemberImage] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onHandleIconClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onHandleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      setMemberImage(previewUrl);
    }
  };

  const uploadImage = async (image?: string) => {
    if (!image) {
      return null;
    }

    // blob URL을 fetch하여 Blob 파일로 변환
    if (typeof image === "string" && image.startsWith("blob:")) {
      const response = await fetch(image);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append("file", blob);
      const imageUrl = await uploadImageToS3(formData);
      return imageUrl;
    }

    // image가 string일 경우 그대로 반환 (이미 업로드된 URL일 가능성)
    if (typeof image === "string") return image;
  };

  const onHandleSave = async () => {
    if (memberName.length === 0) return;

    let imageUrl = (await uploadImage(memberImage)) ?? "";
    const res = await updateMyMemberInfo(memberName, imageUrl);
    if (res.success) {
      navigate(`/setting/${encodeURIComponent("내 정보 관리")}`, { state: { updateSuccess: true } });
    } else {
      showToast(res.message ?? "내 정보 수정에 실패했습니다. 다시 시도해주세요.", "error");
    }
  };

  const onHandleExitWorkspace = async () => {
    const res = await exitWorkspace();
    if (res.success) {
      navigate("/workspace/list", {
        state: {
          exitSuccess: true,
          workspaceName: workspaceInfo?.name ?? "",
        },
      });
    } else {
      if (res.statusCode === 403) {
        showToast(
          res.message ?? "다른 멤버에게 Owner 권한을 부여한 뒤, 워크스페이스 나가기를 재시도해 주세요.",
          "warning"
        );
      } else {
        showToast(res.message ?? "오류가 발생했습니다. 다시 시도해 주세요.", "error");
      }
    }
  };

  useEffect(() => {
    if (location.state) {
      setMemberName(location.state["memberName"]);
      if (location.state["memberImage"] !== "") {
        setMemberImage(location.state["memberImage"]);
      }
    }
  }, []);

  const onHandleExit = () => {
    setOpenModal(true);
  };

  return (
    <Container>
      <TitleContainer>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-netural)"
          style={{ padding: "24px 20px" }}
        >
          내 정보 관리
        </UI.Typography>
        <div style={{ padding: "8px 20px", display: "flex", gap: 8 }}>
          <UI.Button title="나가기" onClick={() => navigate("/setting/내 정보 관리")} type="outlined" />
          <UI.Button title="저장하기" onClick={onHandleSave} disabled={memberName.length === 0} />
        </div>
      </TitleContainer>
      <Rectangle />
      <ProfileContainer>
        <div
          css={css`
            position: relative;
          `}
        >
          <ProfileImg
            src={memberImage ?? "https://mile-storage.s3.ap-northeast-2.amazonaws.com/web/mile-logo-gray.png"}
          />
          <IconContainer onClick={onHandleIconClick}>
            <UI.IconButton.Background iconSize={24} icon="modify" />
          </IconContainer>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept="image/*"
            onChange={onHandleFileChange}
          />
        </div>
        <UI.Typography type="label02" fontStyle="regular" color="var(--alias-color-label-alternative)">
          * 1:1 비율, 권장 규격: 240 x 240px
        </UI.Typography>
      </ProfileContainer>
      <EditContainer>
        <UI.Typography type="label01" fontStyle="bold" color="var(--alias-color-label-alternative">
          이름
        </UI.Typography>
        <UI.Input
          value={memberName}
          width={400}
          showHelpText
          helpText="워크스페이스 내에서 사용할 이름을 작성해주세요."
          maxLength={50}
          showMaxLength={true}
          onChange={(value) => value.length <= 50 && setMemberName(value)}
        />
      </EditContainer>
      <BottonContainer>
        <DeleteBtn>
          <UI.ButtonGroups.Text
            type="negative"
            onClick={onHandleExit}
            title="워크스페이스 나가기"
            size="medium"
            prefixIcon="trashCan"
            showPrefixIcon
          />
        </DeleteBtn>
      </BottonContainer>
      <WorkspaceExitModal
        modalIsOpen={openModal}
        closeModal={() => setOpenModal(false)}
        onChange={onHandleExitWorkspace}
      />
    </Container>
  );
};

export default MyInfoEdit;
